/**
 * Smart Block resolvers — pure functions from (params, projectContext) to
 * ContentBlock[]. Called at project-spec compile time to expand a
 * `smart-token` block into the actual content that ships in the doc.
 *
 * Six built-in kinds:
 *   - related-sections     → auto-list other specs in the project
 *   - section-header       → CSI number + title from thisSpec metadata
 *   - project-identity     → project name/number/client/address block
 *   - issue-date           → current issuance date or compile date
 *   - standards-referenced → regex-scan thisSpec text for ASTM/ANSI/UL/NEMA
 *   - engineer-of-record   → pulled from project team
 *
 * Plus `custom` — query-driven via SmartBlock.querySpec + templateSpec.
 *
 * Determinism: given the same params + context, output MUST be identical.
 * No clock reads, no random ids, no I/O.
 */

import type {
  ContentBlock,
  ProjectContext,
  SmartBlock,
  SmartBlockKind,
  SmartBlockQueryClause,
  SmartBlockQuerySpec,
  SmartBlockResolver,
} from '../../shared/types';
import { renderTemplate } from './template-engine';
import { SAMPLE_PROJECT_ID } from './context-builder';
import {
  RELATED_SECTIONS_KIND,
  readRelatedParams,
  relatedCandidates,
  relatedSectionLines,
} from '../../shared/related-sections';

// ─── Public dispatcher ─────────────────────────────────────────

export function resolveSmartToken(
  block: ContentBlock,
  smartBlock: SmartBlock | undefined,
  context: ProjectContext
): ContentBlock[] {
  const kind = (smartBlock?.kind ?? block.smartBlockKind) as SmartBlockKind | undefined;
  if (!kind) return [unresolved(block, 'Missing smart block kind')];
  const resolver = RESOLVERS[kind];
  if (!resolver) return [unresolved(block, `Unknown kind: ${kind}`)];
  const params = {
    ...(smartBlock?.defaultParams ?? {}),
    ...(block.smartParams ?? {}),
  };
  try {
    const result = resolver(params, context, smartBlock);
    if (!result || result.length === 0) {
      // Related sections with nothing included prints NOTHING — not even the
      // blank paragraph every other kind leaves behind.
      return kind === RELATED_SECTIONS_KIND ? [] : [empty(block)];
    }
    if (kind === RELATED_SECTIONS_KIND) return result.map((b, i) => stampRelated(b, block, i));
    return result.map((b, i) => stamp(b, block, `${block.id}-r${i}`));
  } catch (err: any) {
    return [unresolved(block, `Resolver error: ${err.message ?? err}`)];
  }
}

// ─── Helpers ─────────────────────────────────────────────────────

function stamp(resolved: ContentBlock, anchor: ContentBlock, fallbackId: string): ContentBlock {
  return {
    ...resolved,
    id: resolved.id || fallbackId,
    level: resolved.level ?? anchor.level,
  };
}

/** A Related sections line is an ordinary paragraph AT THE BLOCK'S LEVEL, and
 *  it carries what the block itself says about printing: a block skipped at
 *  export stays skipped, and a whole-block revision (added since the baseline,
 *  or removed) marks every line it expands to. */
function stampRelated(resolved: ContentBlock, anchor: ContentBlock, i: number): ContentBlock {
  const rev = anchor.revision;
  return {
    ...resolved,
    id: `${anchor.id}-r${i}`,
    level: anchor.level,
    ...(anchor.excluded ? { excluded: true } : {}),
    ...(rev && (rev.kind === 'added' || rev.kind === 'removed')
      ? { revision: { kind: rev.kind } }
      : {}),
  };
}

function unresolved(block: ContentBlock, reason: string): ContentBlock {
  return {
    id: `${block.id}-error`,
    level: block.level,
    text: `[Smart Block did not resolve: ${reason}]`,
    italic: true,
  };
}

function empty(block: ContentBlock): ContentBlock {
  return {
    id: `${block.id}-empty`,
    level: block.level,
    text: '',
  };
}

function makeBlock(text: string, level: number, extras: Partial<ContentBlock> = {}): ContentBlock {
  return { id: '', level, text, ...extras };
}

// ─── Built-in resolvers ─────────────────────────────────────────

/**
 * Related Sections — the project's OTHER sections, as the writer picked them
 * in the Designer (the one smart block that returned, Josh 2026-10-02). The
 * selection logic and the line format live in shared/related-sections.ts so
 * the editor's live list and this export are the same function.
 *
 *   seeded for this spec → only the ticked `includeIds` still in the project
 *   unseeded (never, or seeded for a DIFFERENT spec — a paste from
 *     elsewhere) → every other section, minus legacy `excludeIds`
 *   no project → nothing (a master or variant has no sections to list)
 *
 * `ctx.thisSpec.id` is the scope key: the project spec's own id, the same
 * one the Designer and the add-to-project write seed against, so a block
 * pasted in from another project (or arriving via a master/module) reads as
 * unseeded here exactly as it does in the editor.
 *
 * One ordinary paragraph per section, at the block's own level; never the
 * spec itself, never an archived section, and nothing at all when none are
 * included.
 */
const resolveRelatedSections: SmartBlockResolver = (params, ctx) => {
  if (!ctx.project?.id || ctx.project.id === SAMPLE_PROJECT_ID) return [];
  const archived = new Set(ctx.archivedSpecIds ?? []);
  const candidates = relatedCandidates(
    ctx.thisSpec.id,
    ctx.otherSpecsInProject.map((s) => ({
      id: s.id,
      csiNumber: s.csiNumber || s.sectionNumber,
      title: s.title,
      archivedAt: archived.has(s.id) ? 'archived' : null,
    }))
  );
  return relatedSectionLines(readRelatedParams(params), candidates, ctx.thisSpec.id).map((text) =>
    makeBlock(text, 0)
  );
};

/**
 * Section Header — auto-fill the spec's CSI number + title at top of section.
 *
 * Params:
 *   format : 'csi-and-title' | 'csi-only' | 'title-only' (default 'csi-and-title')
 *   bold   : boolean (default true)
 */
const resolveSectionHeader: SmartBlockResolver = (params, ctx) => {
  const format = params.format ?? 'csi-and-title';
  const bold = params.bold ?? true;
  const csi = ctx.thisSpec.csiNumber || ctx.thisSpec.sectionNumber || '';
  const title = ctx.thisSpec.title || '';
  let text: string;
  switch (format) {
    case 'csi-only':
      text = csi;
      break;
    case 'title-only':
      text = title;
      break;
    default:
      text = csi ? `${csi} — ${title}` : title;
  }
  return [makeBlock(text, 0, { bold })];
};

/**
 * Project Identity — multi-line block of project metadata.
 *
 * Params:
 *   fields : Array of which fields to include, in order. Each entry is
 *            one of: 'name' | 'number' | 'client' | 'address' | 'phase'.
 *   labels : boolean — prefix each line with "Project Name:" etc. (default true)
 */
const resolveProjectIdentity: SmartBlockResolver = (params, ctx) => {
  const fields: string[] = params.fields ?? ['name', 'number', 'client', 'address'];
  const labels: boolean = params.labels ?? true;
  const out: ContentBlock[] = [];
  for (const f of fields) {
    let value = '';
    let label = '';
    switch (f) {
      case 'name':
        value = ctx.project.name ?? '';
        label = 'Project Name';
        break;
      case 'number':
        value = ctx.project.number ?? '';
        label = 'Project Number';
        break;
      case 'client':
        value = ctx.client?.name ?? '';
        label = 'Client';
        break;
      case 'address':
        value = ctx.client?.address ?? ctx.project.address ?? '';
        label = 'Address';
        break;
      case 'phase':
        value = ctx.project.phase ?? '';
        label = 'Phase';
        break;
    }
    if (!value) continue;
    out.push(makeBlock(labels ? `${label}: ${value}` : value, 0));
  }
  return out;
};

/**
 * Issue Date — the spec's current issuance date, or compile date as fallback.
 *
 * Params:
 *   format : 'iso' | 'long' | 'short' (default 'long')
 *   label  : string — prefix (default 'Issue Date: ')
 */
const resolveIssueDate: SmartBlockResolver = (params, ctx) => {
  const format = params.format ?? 'long';
  const label = params.label ?? 'Issue Date: ';
  const iso = ctx.currentIssuance?.date ?? ctx.resolvedAt;
  if (!iso) return [];
  const d = new Date(iso);
  if (isNaN(d.getTime())) return [makeBlock(`${label}${iso}`, 0)];
  let formatted: string;
  switch (format) {
    case 'iso':
      formatted = d.toISOString().slice(0, 10);
      break;
    case 'short':
      formatted = d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
      break;
    default:
      formatted = d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
  }
  return [makeBlock(`${label}${formatted}`, 0)];
};

/**
 * Standards Referenced — scan thisSpec's core content for industry-standard
 * citations and list them with deduplication.
 *
 * Params:
 *   header        : string — header line (default "Reference Standards:")
 *   organizations : string[] — which orgs to include. Default ['ASTM','ANSI',
 *                   'UL','NEMA','IEEE','NFPA','IEC','ISO']
 */
const STANDARDS_PATTERNS: Record<string, RegExp> = {
  ASTM: /\bASTM\s+[A-Z]?\d+[A-Z]?(?:[-/]\d+[A-Z]?)?\b/g,
  ANSI: /\bANSI\s+[A-Z]?\d+(?:\.\d+)?(?:[-/]\d+)?\b/g,
  UL: /\bUL\s+\d+[A-Z]?(?:[-/]\d+)?\b/g,
  NEMA: /\bNEMA\s+[A-Z]+\s?\d+(?:\.\d+)?\b/g,
  IEEE: /\bIEEE\s+(?:Std\s+)?\d+(?:\.\d+)?\b/g,
  NFPA: /\bNFPA\s+\d+[A-Z]?\b/g,
  IEC: /\bIEC\s+\d+(?:-\d+)?\b/g,
  ISO: /\bISO\s+\d+(?:-\d+)?(?::\d{4})?\b/g,
};

const resolveStandardsReferenced: SmartBlockResolver = (params, ctx) => {
  const header: string = params.header ?? 'Reference Standards:';
  const orgs: string[] = params.organizations ?? Object.keys(STANDARDS_PATTERNS);
  const found = new Set<string>();
  for (const block of ctx.thisSpec.coreContent ?? []) {
    const text = block.text ?? '';
    for (const org of orgs) {
      const re = STANDARDS_PATTERNS[org];
      if (!re) continue;
      // Reset lastIndex because /g regexes are stateful
      re.lastIndex = 0;
      const matches = text.match(re);
      if (matches) matches.forEach((m) => found.add(m.replace(/\s+/g, ' ').trim()));
    }
  }
  const sorted = [...found].sort();
  const out: ContentBlock[] = [];
  if (header) out.push(makeBlock(header, 0, { bold: true }));
  if (sorted.length === 0) {
    out.push(makeBlock('None referenced in this section.', 1, { italic: true }));
  } else {
    for (const s of sorted) out.push(makeBlock(s, 1));
  }
  return out;
};

/**
 * Engineer of Record — team member responsible for this discipline.
 *
 * Params:
 *   titleMatch : string — case-insensitive substring on team member title
 *                or role (default 'engineer')
 *   format     : 'name-only' | 'name-and-title' | 'name-title-email'
 *                (default 'name-and-title')
 *   label      : string — prefix line (default 'Engineer of Record: ')
 *   firstMatch : boolean — return only first matching team member (default true)
 */
const resolveEngineerOfRecord: SmartBlockResolver = (params, ctx) => {
  const titleMatch: string = (params.titleMatch ?? 'engineer').toLowerCase();
  const format = params.format ?? 'name-and-title';
  const label = params.label ?? 'Engineer of Record: ';
  const firstMatch = params.firstMatch ?? true;
  const matches = ctx.team.filter((m) => {
    const t = (m.title ?? '').toLowerCase();
    const r = (m.role ?? '').toLowerCase();
    return t.includes(titleMatch) || r.includes(titleMatch);
  });
  if (matches.length === 0) {
    return [makeBlock(`${label}(unassigned)`, 0, { italic: true })];
  }
  const picks = firstMatch ? matches.slice(0, 1) : matches;
  return picks.map((m) => {
    let body: string;
    switch (format) {
      case 'name-only':
        body = m.name ?? '';
        break;
      case 'name-title-email':
        body = [m.name, m.title, m.email].filter(Boolean).join(' · ');
        break;
      default:
        body = [m.name, m.title].filter(Boolean).join(', ');
    }
    return makeBlock(`${label}${body}`, 0);
  });
};

/**
 * Custom resolver — runs SmartBlock.querySpec against context, then renders
 * SmartBlock.templateSpec with the result. Author defines both in the Smart
 * Block Creator UI. Returns one ContentBlock per template render (split on
 * newlines for multi-line output).
 */
const resolveCustom: SmartBlockResolver = (params, ctx, smartBlock) => {
  if (!smartBlock) return [];
  const query = smartBlock.querySpec;
  const template = smartBlock.templateSpec ?? '';
  let dataset: any[] | any = null;
  if (query) {
    dataset = runQuery(query, params, ctx);
  }
  // Table layout — bypass the text template and emit a real table block.
  // The Word engine (spec-docx-builder) renders blockType:'table' natively,
  // and the editor previews render it via ResolvedBlocks.
  if (query?.layout === 'table') {
    const rows: any[] = Array.isArray(dataset) ? dataset : dataset ? [dataset] : [];
    const fields =
      query.fields && query.fields.length ? query.fields : rows.length ? Object.keys(rows[0]) : [];
    if (fields.length === 0) {
      return [makeBlock('No data for this table.', 0, { italic: true })];
    }
    const labels = query.labels ?? {};
    const headerRow = fields.map((f) => ({ text: labels[f] ?? f, bold: true }));
    const bodyRows = rows.map((r) =>
      fields.map((f) => ({ text: r?.[f] == null ? '' : String(r[f]) }))
    );
    const cells = [headerRow, ...bodyRows];
    return [
      {
        id: '',
        level: 0,
        text: '',
        blockType: 'table',
        tableData: { rows: cells.length, cols: fields.length, cells, headerRows: 1 },
      } as ContentBlock,
    ];
  }
  if (!template) {
    return [makeBlock('[Custom smart block: no template defined]', 0, { italic: true })];
  }
  const scope = {
    ...params,
    project: ctx.project,
    thisSpec: ctx.thisSpec,
    items: Array.isArray(dataset) ? dataset : [],
    item: !Array.isArray(dataset) ? dataset : null,
    client: ctx.client,
    issuance: ctx.currentIssuance,
    resolvedAt: ctx.resolvedAt,
  };
  const { text, hadErrors } = renderTemplate(template, scope);
  // Split on newlines so each line becomes its own ContentBlock.
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  return lines.map((line, idx) =>
    makeBlock(line, idx === 0 ? 0 : 1, hadErrors ? { italic: true } : {})
  );
};

// ─── Query DSL execution (custom kind) ──────────────────────────

function runQuery(
  spec: SmartBlockQuerySpec,
  params: Record<string, any>,
  ctx: ProjectContext
): any[] {
  let rows: any[];
  switch (spec.from) {
    case 'project-specs':
      rows = [...ctx.otherSpecsInProject];
      break;
    case 'team-members':
      rows = [...ctx.team];
      break;
    case 'project-fields':
      rows = [ctx.project];
      break;
    case 'issuances':
      rows = ctx.currentIssuance ? [ctx.currentIssuance] : [];
      break;
    case 'specs-in-section':
      rows = (ctx.thisSpec.coreContent ?? []).map((b) => ({
        id: b.id,
        level: b.level,
        text: b.text,
      }));
      break;
    default:
      rows = [];
  }
  for (const clause of spec.where ?? []) {
    rows = rows.filter((row) => matchClause(row, clause, params));
  }
  if (spec.sort) {
    const dir = spec.sortDir === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const av = a[spec.sort!];
      const bv = b[spec.sort!];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * dir;
      if (bv == null) return 1 * dir;
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  }
  if (typeof spec.limit === 'number' && spec.limit > 0) {
    rows = rows.slice(0, spec.limit);
  }
  return rows;
}

function matchClause(
  row: Record<string, any>,
  clause: SmartBlockQueryClause,
  params: Record<string, any>
): boolean {
  const field = clause.field;
  const raw = row[field];
  const target = resolveValue(clause.value, params);
  switch (clause.op) {
    case 'equals':
      return raw === target;
    case 'not-equals':
      return raw !== target;
    case 'contains':
      return String(raw ?? '')
        .toLowerCase()
        .includes(String(target ?? '').toLowerCase());
    case 'in':
      return Array.isArray(target) && target.includes(raw);
    case 'not-in':
      return Array.isArray(target) && !target.includes(raw);
    case 'starts-with':
      return String(raw ?? '').startsWith(String(target ?? ''));
    case 'regex':
      try {
        return new RegExp(String(target)).test(String(raw ?? ''));
      } catch {
        return false;
      }
    default:
      return false;
  }
}

function resolveValue(value: any, params: Record<string, any>): any {
  if (typeof value === 'string' && value.startsWith('$')) {
    return params[value.slice(1)];
  }
  return value;
}

// ─── Registry ──────────────────────────────────────────────────────

export const RESOLVERS: Record<SmartBlockKind, SmartBlockResolver> = {
  'related-sections': resolveRelatedSections,
  'section-header': resolveSectionHeader,
  'project-identity': resolveProjectIdentity,
  'issue-date': resolveIssueDate,
  'standards-referenced': resolveStandardsReferenced,
  'engineer-of-record': resolveEngineerOfRecord,
  custom: resolveCustom,
};

export const RESOLVER_KIND_IDS = Object.keys(RESOLVERS) as SmartBlockKind[];
